import { useCallback, useEffect, useRef, useState } from "react";
import { MagnifyingGlass, Plus, PencilSimple, Trash, IdentificationBadge, Clock, Camera } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, authHeaders, BASE_URL } from "@/lib/api";
import PageHeader from "@/admin/components/PageHeader";

const DAY_LABELS = { mon: "Lun", tue: "Mar", wed: "Mié", thu: "Jue", fri: "Vie", sat: "Sáb", sun: "Dom" };
const ALL_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const photoSrc = (u) => (u?.startsWith("/") ? `${BASE_URL}${u}` : u);
const errDetail = (e, fb) => {
    const d = e?.response?.data?.detail;
    return typeof d === "string" ? d : fb;
};

export default function Staff() {
    const [tab, setTab] = useState("employees");
    return (
        <div data-testid="admin-staff">
            <PageHeader kicker="Recursos Humanos" title="Personal" />
            <div className="flex gap-1 bg-white border border-border rounded-full p-1 w-fit mb-6">
                {[["employees", "Empleados", IdentificationBadge], ["shifts", "Turnos", Clock]].map(([id, label, Icon]) => (
                    <button
                        key={id}
                        data-testid={`staff-tab-${id}`}
                        onClick={() => setTab(id)}
                        className={`flex items-center gap-2 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest transition ${
                            tab === id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                        }`}
                    >
                        <Icon size={14} weight="bold" /> {label}
                    </button>
                ))}
            </div>
            {tab === "employees" ? <EmployeesTab /> : <ShiftsTab />}
        </div>
    );
}

/* ------------------------------------------------------------- EMPLOYEES */
function EmployeesTab() {
    const [rows, setRows] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [shifts, setShifts] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async (query = "") => {
        setLoading(true);
        try {
            const [er, sr] = await Promise.all([
                api.get("/admin/employees", { params: query ? { q: query } : {}, headers: authHeaders() }),
                api.get("/admin/shifts", { headers: authHeaders() }),
            ]);
            setRows(er.data.employees || []);
            setShifts(sr.data.shifts || []);
        } catch {
            toast.error("No se pudieron cargar los empleados");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const t = setTimeout(() => load(q.trim()), 350);
        return () => clearTimeout(t);
    }, [q, load]);

    const del = async (e) => {
        if (!window.confirm(`¿Eliminar a "${e.name}"? Se recomienda marcarlo como Inactivo en lugar de eliminar.`)) return;
        try {
            await api.delete(`/admin/employees/${e.id}`, { headers: authHeaders() });
            toast.success("Empleado eliminado");
            load(q.trim());
        } catch {
            toast.error("No se pudo eliminar");
        }
    };

    const toggleActive = async (e) => {
        try {
            const { shift, ...rest } = e;
            await api.put(`/admin/employees/${e.id}`, { ...rest, active: !e.active }, { headers: authHeaders() });
            toast.success(e.active ? "Empleado marcado inactivo" : "Empleado activado");
            load(q.trim());
        } catch (err) {
            toast.error(errDetail(err, "No se pudo actualizar"));
        }
    };

    return (
        <div data-testid="employees-tab">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                <div className="relative max-w-md flex-1 min-w-[220px]">
                    <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        data-testid="employees-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Buscar por nombre, número, departamento..."
                        className="pl-9 rounded-full bg-white"
                    />
                </div>
                <button
                    data-testid="employee-add-btn"
                    onClick={() => setEditing({})}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:-translate-y-0.5 transition"
                >
                    <Plus size={15} weight="bold" /> Nuevo empleado
                </button>
            </div>

            <div className="bg-white rounded-2xl border border-border overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-muted-foreground">Cargando...</div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-muted-foreground" data-testid="employees-empty">
                        <IdentificationBadge size={40} weight="duotone" className="mx-auto mb-3 opacity-40" />
                        {q ? "Sin resultados." : "Todavía no hay empleados. Crea el primero."}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="employees-table">
                            <thead className="bg-muted/40">
                                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                                    <th className="px-4 py-3">Empleado</th>
                                    <th className="px-4 py-3">Número</th>
                                    <th className="px-4 py-3">Departamento</th>
                                    <th className="px-4 py-3">Cargo</th>
                                    <th className="px-4 py-3">Turno</th>
                                    <th className="px-4 py-3">Ingreso</th>
                                    <th className="px-4 py-3">Estado</th>
                                    <th className="px-4 py-3 text-right">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((e, i) => (
                                    <tr key={e.id} className="border-t border-border" data-testid={`employee-row-${i}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                {e.photo_url ? (
                                                    <img src={photoSrc(e.photo_url)} alt="" className="h-9 w-9 rounded-full object-cover" />
                                                ) : (
                                                    <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-black text-sm">
                                                        {(e.name || "?").slice(0, 1)}
                                                    </div>
                                                )}
                                                <span className="font-bold">{e.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-xs">{e.employee_number}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{e.department || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground">{e.position || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs">{e.shift?.name || "—"}</td>
                                        <td className="px-4 py-3 text-muted-foreground text-xs whitespace-nowrap">{e.hire_date || "—"}</td>
                                        <td className="px-4 py-3">
                                            <button
                                                data-testid={`employee-toggle-${i}`}
                                                onClick={() => toggleActive(e)}
                                                className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border transition ${
                                                    e.active
                                                        ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                                        : "text-muted-foreground bg-muted border-border"
                                                }`}
                                            >
                                                {e.active ? "Activo" : "Inactivo"}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                            <button data-testid={`employee-edit-${i}`} onClick={() => setEditing(e)} className="h-8 w-8 inline-grid place-items-center rounded-full hover:bg-muted transition" title="Editar">
                                                <PencilSimple size={15} weight="bold" />
                                            </button>
                                            <button data-testid={`employee-delete-${i}`} onClick={() => del(e)} className="h-8 w-8 inline-grid place-items-center rounded-full text-muted-foreground hover:text-destructive hover:bg-red-50 transition" title="Eliminar">
                                                <Trash size={15} weight="bold" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {editing !== null && (
                <EmployeeFormDialog
                    employee={editing}
                    shifts={shifts}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(q.trim()); }}
                />
            )}
        </div>
    );
}

function EmployeeFormDialog({ employee, shifts, onClose, onSaved }) {
    const isNew = !employee.id;
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        employee_number: employee.employee_number || "",
        name: employee.name || "",
        department: employee.department || "",
        position: employee.position || "",
        shift_id: employee.shift_id || "",
        hire_date: employee.hire_date || "",
        active: employee.active !== false,
        photo_url: employee.photo_url || "",
        pin: employee.pin || "",
        phone: employee.phone || "",
        email: employee.email || "",
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const uploadPhoto = async (file) => {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/admin/upload/image?purpose=employee", fd, { headers: authHeaders() });
            set("photo_url", r.data.url);
            toast.success("Foto subida");
        } catch {
            toast.error("No se pudo subir la foto");
        } finally {
            setUploading(false);
        }
    };

    const save = async () => {
        if (!form.name.trim() || !form.employee_number.trim()) return toast.error("Nombre y número de empleado son requeridos");
        if (!/^\d{4,6}$/.test(form.pin)) return toast.error("El PIN debe ser de 4 a 6 dígitos");
        setBusy(true);
        try {
            const payload = {
                ...form,
                shift_id: form.shift_id || null,
                hire_date: form.hire_date || null,
                email: form.email.trim() || null,
            };
            if (isNew) {
                await api.post("/admin/employees", payload, { headers: authHeaders() });
                toast.success("Empleado creado");
            } else {
                await api.put(`/admin/employees/${employee.id}`, payload, { headers: authHeaders() });
                toast.success("Empleado actualizado");
            }
            onSaved();
        } catch (e) {
            toast.error(errDetail(e, "No se pudo guardar"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="employee-form-dialog" className="sm:max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">
                        {isNew ? "Nuevo empleado" : "Editar empleado"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Ficha del empleado</DialogDescription>
                </DialogHeader>

                <div className="flex items-center gap-4">
                    <button
                        data-testid="employee-photo-btn"
                        onClick={() => fileRef.current?.click()}
                        className="relative h-20 w-20 rounded-full overflow-hidden bg-muted grid place-items-center border-2 border-dashed border-border hover:border-primary transition shrink-0"
                        title="Subir fotografía"
                    >
                        {form.photo_url ? (
                            <img src={photoSrc(form.photo_url)} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <Camera size={26} className="text-muted-foreground" />
                        )}
                        {uploading && <span className="absolute inset-0 bg-black/50 grid place-items-center text-white text-[10px] font-bold">...</span>}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="employee-photo-input" />
                    <div className="flex-1 space-y-2">
                        <Input placeholder="Nombre completo *" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="employee-form-name" />
                        <Input placeholder="Número de empleado * (ej. EMP-001)" value={form.employee_number} onChange={(e) => set("employee_number", e.target.value)} data-testid="employee-form-number" />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-1">
                    <Input placeholder="Departamento" value={form.department} onChange={(e) => set("department", e.target.value)} data-testid="employee-form-department" />
                    <Input placeholder="Cargo" value={form.position} onChange={(e) => set("position", e.target.value)} data-testid="employee-form-position" />
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Turno</label>
                        <select
                            data-testid="employee-form-shift"
                            value={form.shift_id}
                            onChange={(e) => set("shift_id", e.target.value)}
                            className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                        >
                            <option value="">Sin turno</option>
                            {shifts.map((s) => (
                                <option key={s.id} value={s.id}>{s.name} ({s.entry}–{s.exit})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Fecha de ingreso</label>
                        <Input type="date" value={form.hire_date} onChange={(e) => set("hire_date", e.target.value)} data-testid="employee-form-hiredate" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">PIN checador (4-6 dígitos) *</label>
                        <Input
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="Ej. 1234"
                            value={form.pin}
                            onChange={(e) => set("pin", e.target.value.replace(/\D/g, ""))}
                            data-testid="employee-form-pin"
                        />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Teléfono</label>
                        <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="employee-form-phone" />
                    </div>
                    <Input className="col-span-2" type="email" placeholder="Correo (opcional)" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="employee-form-email" />
                </div>

                <label className="flex items-center gap-2 text-sm font-semibold mt-1 cursor-pointer">
                    <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="employee-form-active" className="h-4 w-4" />
                    Empleado activo
                </label>

                <Button
                    data-testid="employee-form-save"
                    onClick={save}
                    disabled={busy || uploading}
                    className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-xs py-5"
                >
                    {busy ? "..." : isNew ? "Crear empleado" : "Guardar cambios"}
                </Button>
            </DialogContent>
        </Dialog>
    );
}

/* ------------------------------------------------------------- SHIFTS */
function ShiftsTab() {
    const [shifts, setShifts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/shifts", { headers: authHeaders() });
            setShifts(r.data.shifts || []);
        } catch {
            toast.error("No se pudieron cargar los turnos");
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const del = async (s) => {
        if (!window.confirm(`¿Eliminar el turno "${s.name}"?`)) return;
        try {
            await api.delete(`/admin/shifts/${s.id}`, { headers: authHeaders() });
            toast.success("Turno eliminado");
            load();
        } catch (e) {
            toast.error(errDetail(e, "No se pudo eliminar"));
        }
    };

    return (
        <div data-testid="shifts-tab">
            <div className="flex justify-end mb-4">
                <button
                    data-testid="shift-add-btn"
                    onClick={() => setEditing({})}
                    className="inline-flex items-center gap-2 rounded-full bg-foreground text-background px-5 py-2.5 font-bold uppercase tracking-widest text-xs hover:-translate-y-0.5 transition"
                >
                    <Plus size={15} weight="bold" /> Nuevo turno
                </button>
            </div>
            {loading ? (
                <div className="p-12 text-center text-muted-foreground bg-white rounded-2xl border border-border">Cargando...</div>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {shifts.map((s, i) => (
                        <div key={s.id} className="bg-white rounded-2xl border border-border p-5" data-testid={`shift-card-${i}`}>
                            <div className="flex items-start justify-between">
                                <p className="font-display font-black text-lg">{s.name}</p>
                                <div className="flex gap-1">
                                    <button data-testid={`shift-edit-${i}`} onClick={() => setEditing(s)} className="h-7 w-7 grid place-items-center rounded-full hover:bg-muted transition">
                                        <PencilSimple size={14} weight="bold" />
                                    </button>
                                    <button data-testid={`shift-delete-${i}`} onClick={() => del(s)} className="h-7 w-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive transition">
                                        <Trash size={14} weight="bold" />
                                    </button>
                                </div>
                            </div>
                            <div className="mt-3 space-y-1.5 text-sm">
                                <p><span className="text-muted-foreground">Entrada:</span> <b>{s.entry}</b> · <span className="text-muted-foreground">Salida:</span> <b>{s.exit}</b></p>
                                <p className="text-muted-foreground text-xs">
                                    Comida: {s.lunch_start ? `${s.lunch_start} – ${s.lunch_end}` : "Sin comida"} · Tolerancia: {s.tolerance_minutes} min
                                </p>
                                <div className="flex gap-1 flex-wrap pt-1">
                                    {ALL_DAYS.map((d) => (
                                        <span key={d} className={`text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${(s.days || []).includes(d) ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground/50"}`}>
                                            {DAY_LABELS[d]}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {editing !== null && (
                <ShiftFormDialog shift={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
            )}
        </div>
    );
}

function ShiftFormDialog({ shift, onClose, onSaved }) {
    const isNew = !shift.id;
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: shift.name || "",
        entry: shift.entry || "08:00",
        lunch_start: shift.lunch_start || "",
        lunch_end: shift.lunch_end || "",
        exit: shift.exit || "17:00",
        tolerance_minutes: shift.tolerance_minutes ?? 10,
        days: shift.days || ["mon", "tue", "wed", "thu", "fri", "sat"],
    });
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleDay = (d) =>
        set("days", form.days.includes(d) ? form.days.filter((x) => x !== d) : [...form.days, d]);

    const save = async () => {
        if (!form.name.trim()) return toast.error("El nombre es requerido");
        if ((form.lunch_start && !form.lunch_end) || (!form.lunch_start && form.lunch_end))
            return toast.error("Define inicio y fin de comida, o deja ambos vacíos");
        setBusy(true);
        try {
            const payload = {
                ...form,
                lunch_start: form.lunch_start || null,
                lunch_end: form.lunch_end || null,
                tolerance_minutes: Number(form.tolerance_minutes) || 0,
            };
            if (isNew) {
                await api.post("/admin/shifts", payload, { headers: authHeaders() });
                toast.success("Turno creado");
            } else {
                await api.put(`/admin/shifts/${shift.id}`, payload, { headers: authHeaders() });
                toast.success("Turno actualizado");
            }
            onSaved();
        } catch (e) {
            toast.error(errDetail(e, "No se pudo guardar"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="shift-form-dialog" className="sm:max-w-md rounded-2xl">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl font-black tracking-tight">
                        {isNew ? "Nuevo turno" : "Editar turno"}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Horario del turno</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <Input placeholder="Nombre (ej. Turno Matutino)" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="shift-form-name" />
                    <div className="grid grid-cols-2 gap-3">
                        <TimeField label="Entrada" value={form.entry} onChange={(v) => set("entry", v)} testid="shift-form-entry" />
                        <TimeField label="Salida" value={form.exit} onChange={(v) => set("exit", v)} testid="shift-form-exit" />
                        <TimeField label="Comida (inicio)" value={form.lunch_start} onChange={(v) => set("lunch_start", v)} testid="shift-form-lunchstart" />
                        <TimeField label="Regreso de comida" value={form.lunch_end} onChange={(v) => set("lunch_end", v)} testid="shift-form-lunchend" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tolerancia de retardo (min)</label>
                        <Input type="number" min={0} max={120} value={form.tolerance_minutes} onChange={(e) => set("tolerance_minutes", e.target.value)} data-testid="shift-form-tolerance" />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Días laborales</label>
                        <div className="flex gap-1.5 flex-wrap mt-1">
                            {ALL_DAYS.map((d) => (
                                <button
                                    key={d}
                                    data-testid={`shift-day-${d}`}
                                    onClick={() => toggleDay(d)}
                                    className={`text-xs font-bold uppercase rounded-full px-3 py-1.5 border transition ${
                                        form.days.includes(d)
                                            ? "bg-foreground text-background border-foreground"
                                            : "bg-white text-muted-foreground border-border"
                                    }`}
                                >
                                    {DAY_LABELS[d]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <Button
                        data-testid="shift-form-save"
                        onClick={save}
                        disabled={busy}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase tracking-widest text-xs py-5"
                    >
                        {busy ? "..." : isNew ? "Crear turno" : "Guardar cambios"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function TimeField({ label, value, onChange, testid }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</label>
            <Input type="time" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        </div>
    );
}
